// Character ownership + sharing, for display and for filtering (ENG-1028).
//
// Pure and injectable, the same contract as characterProperties.js next door:
// every decision here is testable without mounting React or touching a server.
//
// The rule this module enforces, inherited from characterProperties.js and from
// the CLAUDE.md no-silent-fallbacks rule: a server that does not report sharing
// at all is a DIFFERENT situation from a character that is shared, and the two
// must never collapse into the same rendering. An asset-manager from before
// ENG-1028 emits no `shared` field, and defaulting that to `true` would paint
// every character in the org with a confident "Shared" badge that nothing on the
// server actually said — the exact fabrication the properties panel exists to
// avoid. Absent reads as unknown.

/** Sharing states a card or panel can render. */
export const SHARING = {
  PRIVATE: 'private',
  SHARED: 'shared',
  UNKNOWN: 'unknown',
}

/**
 * What the server said about this character's sharing.
 *
 * @param {object} character a row from GET /api/characters
 * @returns {'private'|'shared'|'unknown'}
 */
export function sharingState(character) {
  if (!character || typeof character.shared !== 'boolean') return SHARING.UNKNOWN
  return character.shared ? SHARING.SHARED : SHARING.PRIVATE
}

/**
 * Is this character owned by the signed-in user?
 *
 * Compared on the owner id, never the email: the email is a snapshot captured at
 * write time (the server cannot resolve an id to a name later), so a person who
 * changed their address would stop matching their own characters.
 *
 * @param {object} character
 * @param {string|null|undefined} userId the viewer's auth.users id
 */
export function isOwnedBy(character, userId) {
  const owner = typeof character?.ownerId === 'string' ? character.ownerId.trim() : ''
  const me = typeof userId === 'string' ? userId.trim() : ''
  // Both-empty must NOT match. An ownerless legacy character would otherwise
  // read as owned by any viewer whose id the client had not yet resolved, which
  // is every viewer during the first render.
  return owner !== '' && me !== '' && owner === me
}

/** Ownerless: shared and nobody has claimed it — the legacy state 00041 left in
    place deliberately, and the one an org-admin resolves. */
export function isOwnerless(character) {
  const owner = typeof character?.ownerId === 'string' ? character.ownerId.trim() : ''
  return owner === ''
}

/** The launcher's scope tabs. ALL is the default so the first render matches
    what the launcher showed before this feature existed. */
export const SCOPE = {
  ALL: 'all',
  MINE: 'mine',
  SHARED: 'shared',
}

/**
 * Filter predicate for the launcher's scope tabs.
 *
 * MINE means "owned by me", not "private": a character you own and have already
 * published is still yours, and dropping it out of your own tab the moment you
 * share it would make the tab useless as a working set.
 *
 * SHARED means visible to the org — which includes your own published ones, for
 * the same reason.
 *
 * A character whose sharing is UNKNOWN (an older server) passes every scope: it
 * is better to show it in a tab it may not belong to than to make it vanish
 * from a list it was visible in yesterday.
 */
export function matchesScope(character, scope, userId) {
  if (scope === SCOPE.MINE) {
    return isOwnedBy(character, userId)
  }
  if (scope === SCOPE.SHARED) {
    return sharingState(character) !== SHARING.PRIVATE
  }
  return true
}

/**
 * The badge a launcher card shows, or null for no badge.
 *
 * Shared characters get NO badge on purpose: shared is the norm, and a badge on
 * every card is noise that makes the private ones harder to spot — which is the
 * one thing the badge exists for.
 */
export function cardBadge(character, userId) {
  const state = sharingState(character)
  if (state === SHARING.PRIVATE) {
    return isOwnedBy(character, userId)
      ? { key: 'private', label: 'PRIVATE', title: 'Only you can see this character' }
      : {
          key: 'private-other',
          label: 'PRIVATE',
          // Reachable only in the org-admin others-private view. Named
          // differently from your own so an admin can tell at a glance whose
          // workspace they are looking at.
          title: ownerLabel(character)
            ? `Private to ${ownerLabel(character)}`
            : 'Private to another user',
        }
  }
  return null
}

/**
 * Display label for a character's owner.
 *
 * Email first because it is the only part a person can read; a bare id is
 * shortened but still shown, because it is evidence something was recorded and
 * is enough to match against an audit log. Mirrors actorLabel() in
 * characterProperties.js — the same reasoning, applied to the owner pair.
 *
 * @returns {string|null} null when the character is ownerless.
 */
export function ownerLabel(character) {
  const email = typeof character?.ownerEmail === 'string' ? character.ownerEmail.trim() : ''
  if (email) return email
  const id = typeof character?.ownerId === 'string' ? character.ownerId.trim() : ''
  if (!id) return null
  return id.length > 12 ? `${id.slice(0, 8)}…` : id
}

const NO_OWNER = 'nobody — this character predates personal workspaces'
const NO_SHARING_FIELD = 'not reported by this server — the API predates ENG-1028'

/**
 * The two Properties-panel rows this feature adds, in the same shape
 * characterPropertyRows() produces so the panel renders them with one Row.
 */
export function sharingPropertyRows(character) {
  if (!character) return []
  const state = sharingState(character)
  const owner = ownerLabel(character)

  return [
    {
      key: 'shared',
      label: 'Visibility',
      value:
        state === SHARING.UNKNOWN
          ? NO_SHARING_FIELD
          : state === SHARING.SHARED
            ? 'Shared with the org'
            : 'Private',
      title: null,
      unknown: state === SHARING.UNKNOWN,
    },
    {
      key: 'owner',
      label: 'Owner',
      value: owner ?? NO_OWNER,
      title: typeof character.ownerId === 'string' && character.ownerId.trim() ? character.ownerId.trim() : null,
      unknown: owner == null,
    },
  ]
}

/**
 * Can the viewer change this character's sharing, and if not, why not?
 *
 * Mirrors the server's rule (app/services/character_visual/ownership.go) rather
 * than inventing a client-side one: owner-only, with an org-admin exception, and
 * an ownerless character refused to everyone until an admin assigns an owner.
 * The client copy exists to explain the refusal in place instead of surfacing a
 * 403 after the click — the server remains the enforcement point.
 *
 * @returns {{allowed: boolean, reason: string|null}}
 */
export function canChangeSharing(character, userId, isOrgAdmin) {
  if (sharingState(character) === SHARING.UNKNOWN) {
    return { allowed: false, reason: 'This server does not support personal workspaces yet.' }
  }
  if (isOwnerless(character)) {
    // Refused for everyone, admin included — but the reason now points at the
    // claim, which anyone can do, instead of at a role that may be held by
    // nobody. Going private stays a deliberate second step after claiming, so
    // it is never a side effect of pressing a visibility control on a character
    // you have not taken responsibility for.
    return {
      allowed: false,
      reason: 'Nobody owns this character yet. Claim it first, then you can change its visibility.',
    }
  }
  if (isOwnedBy(character, userId) || isOrgAdmin) return { allowed: true, reason: null }
  return { allowed: false, reason: `Only ${ownerLabel(character) ?? 'the owner'} or an org-admin can change this.` }
}

/**
 * The sentence shown in the un-share confirmation.
 *
 * Built from the server's share-impact numbers, and deliberately explicit about
 * the case where there are none: "nobody else has touched it" is a materially
 * different decision from "we cannot tell", and the impact endpoint can only
 * count assets whose created_by was recorded (00027 onwards). Assets older than
 * that carry no actor, so silence is not proof of solitude.
 *
 * @param {object|null} impact response from GET /share-impact, or null while loading
 */
export function unshareWarning(impact) {
  if (!impact) return 'Checking what this would affect…'
  const { otherActors = 0, assetCount = 0, blueprints = 0 } = impact
  const parts = []
  if (assetCount > 0) parts.push(`${assetCount} asset${assetCount === 1 ? '' : 's'}`)
  if (blueprints > 0) parts.push(`${blueprints} blueprint${blueprints === 1 ? '' : 's'}`)
  const contents = parts.length > 0 ? ` It holds ${parts.join(' and ')}.` : ''

  if (otherActors > 0) {
    return `${otherActors} other ${otherActors === 1 ? 'person has' : 'people have'} worked on this character. ` +
      `Making it private removes it from everyone else in the org immediately.${contents}`
  }
  return 'Nobody else appears to have worked on this character, but assets created before ' +
    'provenance tracking record no author — so this cannot rule it out. Making it private ' +
    `removes it from everyone else in the org immediately.${contents}`
}

/**
 * Does this role set grant org-admin authority?
 *
 * Mirrors domain.IsOrgAdmin in avatar-asset-manager — super-admin or org-admin
 * ONLY. Deliberately NOT the wider AAMAdminRoles set, which also admits plain
 * "user" while AAM is single-tenant: that set gates whether you may call the API
 * at all, and treating it as admin authority here would offer every signed-in
 * user a control over everyone else's private characters that the server would
 * then refuse. Client-side this is presentation only; the server decides.
 *
 * @param {string[]|null|undefined} roles from /auth/me
 */
export function hasOrgAdminRole(roles) {
  if (!Array.isArray(roles)) return false
  return roles.some(r => r === 'super-admin' || r === 'org-admin')
}

/**
 * May the viewer claim or assign this character's owner, and if not, why not?
 *
 * Mirrors the server rule in app/services/character_visual/ownership.go, which
 * depends on the character's CURRENT state rather than on the role alone:
 *
 *   - ownerless  → any org member may claim it FOR THEMSELVES
 *   - owned      → org-admin only (reassignment takes someone else's work)
 *   - clearing   → org-admin only (the un-claim)
 *
 * Admin-only claiming was the original rule and it bricked the feature: every
 * character predating migration 00041 is ownerless, an ownerless character
 * cannot be made private, so if no account holds the role then nothing in the
 * existing catalogue can ever be cleaned up.
 *
 * @returns {{allowed: boolean, reason: string|null, isClaim: boolean}}
 *   isClaim distinguishes "take it for myself" from "assign to someone", so the
 *   UI can label the button for what it actually does.
 */
export function canAssignOwner(character, userId, isOrgAdmin) {
  if (sharingState(character) === SHARING.UNKNOWN) {
    return { allowed: false, reason: 'This server does not support personal workspaces yet.', isClaim: false }
  }
  if (isOwnerless(character)) {
    // A viewer whose id has not resolved yet cannot claim: the server records
    // the CALLER as owner, and offering the button before we know who that is
    // would show a control whose outcome we cannot state.
    if (!userId && !isOrgAdmin) {
      return { allowed: false, reason: 'Still identifying you — reload if this persists.', isClaim: true }
    }
    return { allowed: true, reason: null, isClaim: true }
  }
  if (isOrgAdmin) return { allowed: true, reason: null, isClaim: false }
  return {
    allowed: false,
    reason: `${ownerLabel(character) ?? 'Someone'} owns this character. Only an org-admin can reassign it.`,
    isClaim: false,
  }
}
